import { useMemo } from 'react';
import { DayData, ParsedResult } from '../common.types';
import { ASPECT_RATIO, HERO_SECTION_HEIGHTS } from '../constants';
import { COMPONENT_KEY, FormFieldEnum, VARIANT_KEY } from '../enums';

export const handleNavigationPreviewMode = (link: any) => {
  if (link.internal) {
    const url = new URL(window.location.href);
    url.searchParams.set('pageId', link.pageId);
    if (link.sectionId) {
      url.searchParams.set('sectionId', link.sectionId);
    } else {
      url.searchParams.delete('sectionId');
    }
    window.history.replaceState(null, '', url.toString());
    window.dispatchEvent(new CustomEvent('urlSearchParamsChanged'));
  } else if (link.link) {
    window.open(link.link, '_blank');
  }
};

export const parseTimings = (data: Record<string, DayData>, toggle: boolean): ParsedResult => {
  const daysOrder = ['mon', 'tues', 'wed', 'thurs', 'fri', 'sat', 'sun'];

  const dayNames: Record<string, string> = {
    mon: 'Monday',
    tues: 'Tuesday',
    wed: 'Wednesday',
    thurs: 'Thursday',
    fri: 'Friday',
    sat: 'Saturday',
    sun: 'Sunday',
  };

  const openDays: Record<string, string> = {};
  const closedDays: Record<string, string> = {};

  let currentGroup: string[] = []; // store day keys like 'mon'
  let currentTimings = '';

  const flushGroup = () => {
    if (currentGroup.length > 0) {
      const formattedGroup = formatGroup(currentGroup.map((key) => dayNames[key]));
      openDays[formattedGroup] = currentTimings;
    }
    currentGroup = [];
  };

  for (let i = 0; i < daysOrder.length; i++) {
    const dayKey = daysOrder[i];
    const dayData = data[dayKey];
    const dayName = dayNames[dayKey];

    if (!dayData || dayData.status === 'closed') {
      flushGroup();
      closedDays[dayName] = 'closed';
      continue;
    }

    const timings = `${dayData.open} - ${dayData.close}`;

    const lastDayKey = currentGroup[currentGroup.length - 1];
    const lastIndex = daysOrder.indexOf(lastDayKey);

    if (currentGroup.length > 0 && timings === currentTimings && i === lastIndex + 1) {
      currentGroup.push(dayKey);
    } else {
      flushGroup();
      currentGroup = [dayKey];
      currentTimings = timings;
    }
  }

  flushGroup();

  return { toggle, openDays, closedDays };
};

const formatGroup = (group: string[]): string => {
  if (group.length === 7) {
    return 'Monday - Sunday';
  } else if (group.length > 1) {
    return `${group[0]} - ${group[group.length - 1]}`;
  } else {
    return group[0];
  }
};

export function formatFormFieldName(fieldName: FormFieldEnum): string {
  return fieldName
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function invertColor(hex: string): string {
  if (hex) {
    hex = hex.replace('#', '');
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);
    r = 255 - r;
    g = 255 - g;
    b = 255 - b;
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()}`;
  }
  return '';
}

export function getTextColorBasedOnBackgroundHex(hex: string) {
  // Remove the hash if present
  if (hex) {
    hex = hex.replace('#', '');

    // Convert hex to RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Calculate relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    // Return white for dark backgrounds and black for light backgrounds
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
  }
}

export function hexToRgba(hex: string, alpha: number): string {
  if (hex) {
    hex = hex.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return '';
}

export const apiClient = (apiUrl: string) => {
  return {
    get: async <T>(url: string, options: RequestInit = {}): Promise<T> => {
      const response = await fetch(`${apiUrl}${url}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...(options.headers || {}),
        },
      });
      return await response.json();
    },
  };
};

export const ensureHttpsInUrl = (url: string) => {
  if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
    return `https://${url}`;
  }
  return url;
};

export const pxToNumber = (pxString: string) => {
  const numericValue = parseFloat(pxString.toString().replace('px', ''));
  if (isNaN(numericValue)) {
    console.error(`Invalid px value: ${pxString}`);
    return null;
  }
  return numericValue;
};

export function removeTransparency(hex: string): string {
  // Normalize hex to remove the leading '#' if present
  const cleanHex = hex.replace(/^#/, '');

  // Handle 8-digit (RRGGBBAA) or 4-digit (RGBA) hex codes
  if (cleanHex.length === 8) {
    // Remove last 2 characters (alpha)
    return `#${cleanHex.slice(0, 6)}`;
  } else if (cleanHex.length === 4) {
    // Remove last character (alpha) and expand shorthand (#RGBA → #RRGGBB)
    const r = cleanHex[0];
    const g = cleanHex[1];
    const b = cleanHex[2];
    return `#${r}${r}${g}${g}${b}${b}`;
  } else if (cleanHex.length === 6 || cleanHex.length === 3) {
    // Already opaque, just return normalized
    return `#${cleanHex}`;
  } else {
    throw new Error('Invalid hex color format');
  }
}

export const getResponsiveFontStyles = (fontSize: string) => {
  const fontSizeNumber = typeof fontSize === 'string' ? pxToNumber(fontSize) : fontSize;
  if (!fontSizeNumber) {
    return {
      tablet: {
        fontSize,
      },
      mobile: {
        fontSize,
      },
    };
  }
  return {
    tablet: {
      fontSize: `${Math.round(fontSizeNumber * 0.9)}px`,
      lineHeight: `${Math.round(fontSizeNumber * 0.9 * 1.3)}px`,
    },
    mobile: {
      fontSize: `${Math.round(fontSizeNumber * 0.6)}px`,
      lineHeight: `${Math.round(fontSizeNumber * 0.6)}px`,
    },
  };
};

export const resolveSectionBackgroundColor = (
  componentConfig: {
    background?: { value?: string };
    backgroundImage?: { toggle?: boolean; value?: string; overlayColor?: string };
  },
  globalStyles?: { backgroundColor?: string }
) => {
  if (componentConfig?.backgroundImage?.toggle && componentConfig?.backgroundImage?.value) {
    return componentConfig.backgroundImage.overlayColor || '#ffffff';
  }

  return componentConfig?.background?.value || globalStyles?.backgroundColor || '#ffffff';
};

export function truncateString(input: string, max = 16): string {
  if (input.length > max) {
    return input.slice(0, max) + '...';
  }
  return input;
}

export const generateUrl = (link: any, pageUrls: { [key: string]: string }) => {
  if (!link) return '';

  if (link.internal && pageUrls) {
    const basePath = pageUrls[link.pageId] || '';
    const sectionHash = link.sectionId ? `#${link.sectionId}` : '';
    return `${basePath}${sectionHash}`;
  }

  return link.link || '';
};

export const restaurantUrl = (
  onlineOrderingUrl: string,
  businessAlias: string,
  restaurantAlias: string,
  giftCardId?: string
) => {
  const sanitizedOnlineOrderingUrl = onlineOrderingUrl.includes('online-ordering')
    ? onlineOrderingUrl.replace(/\/?online-ordering/, '')
    : onlineOrderingUrl;

  return `${sanitizedOnlineOrderingUrl}/${businessAlias}/${restaurantAlias}/gift-card`;
};

export const createBackgroundImageStyle = (backgroundImage: Record<string, any>) => {
  if (backgroundImage?.toggle && backgroundImage.value.length > 0) {
    return {
      backgroundImage: `linear-gradient(0deg, ${hexToRgba(
        backgroundImage.overlayColor,
        backgroundImage.overlayOpacity
      )}, ${hexToRgba(backgroundImage.overlayColor, backgroundImage.overlayOpacity)}), url(${backgroundImage.value})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    };
  } else {
    return {};
  }
};

export const useAspectRatio = (data: any): string[] => {
  return useMemo(() => {
    if (
      data.activeComponent.type === COMPONENT_KEY.HERO &&
      (data.activeComponent.variant === VARIANT_KEY.VARIANT1 || data.activeComponent.variant === VARIANT_KEY.VARIANT2)
    ) {
      const sectionHeight = data.pages[data.activePage].MainComponent.props.config.sectionHeight;
      const typeConfig = ASPECT_RATIO[data.activeComponent.type as keyof typeof ASPECT_RATIO];

      if (sectionHeight === HERO_SECTION_HEIGHTS.DEFAULT || sectionHeight === HERO_SECTION_HEIGHTS.FULL_SCREEN_HEIGHT) {
        return typeConfig[data.activeComponent.variant as keyof typeof typeConfig];
      }
    }
    const typeConfig = ASPECT_RATIO[data.activeComponent.type as keyof typeof ASPECT_RATIO];
    if (typeConfig && typeConfig[data.activeComponent.variant as keyof typeof typeConfig]) {
      return typeConfig[data.activeComponent.variant as keyof typeof typeConfig];
    }
    return ['1 / 1'];
  }, [data.activeComponent, data.pages, data.activePage]);
};

/**
 * Returns a selected state color based on an input color
 * @param {string} baseColor - The original color in hex format (e.g., '#FF5733')
 * @param {number} [darkenAmount=0.15] - How much to darken for the selected state (0-1)
 * @return {string} - The selected state color in hex format
 */
export const getSelectedStateColor = (baseColor: string, darkenAmount = 0.25) => {
  // Remove # if present
  const hex = baseColor.replace('#', '');

  // Convert hex to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Calculate luminance to determine if the color is very dark
  // Using the perceived luminance formula: 0.299*R + 0.587*G + 0.114*B
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // If color is very dark (luminance < 0.1), lighten it instead
  if (luminance < 0.1) {
    // Lighten the color
    const lightenAmount = 0.2; // Amount to lighten dark colors
    const lightenColor = (colorValue: number) => {
      return Math.min(255, Math.floor(colorValue + (255 - colorValue) * lightenAmount));
    };

    const lighterR = lightenColor(r);
    const lighterG = lightenColor(g);
    const lighterB = lightenColor(b);

    // Convert back to hex
    const toHex = (c: number) => {
      const hex = c.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${toHex(lighterR)}${toHex(lighterG)}${toHex(lighterB)}`;
  } else {
    // For normal colors, darken as before
    const darkenColor = (colorValue: number) => {
      return Math.max(0, Math.floor(colorValue * (1 - darkenAmount)));
    };

    const darkerR = darkenColor(r);
    const darkerG = darkenColor(g);
    const darkerB = darkenColor(b);

    const toHex = (c: number) => {
      const hex = c.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${toHex(darkerR)}${toHex(darkerG)}${toHex(darkerB)}`;
  }
};
